/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  CallingIdentity,
  useGetPromptRatingAggregation,
  useGetPromptRevision,
  useGetPublishedPromptRevision,
  usePublishPromptRevision,
} from '@agent-assist/api-typescript-react-query-hooks';
import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {Button, Card, CardContent, CardHeader, Chip} from '@mui/material';
import {FC, useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {ErrorPage} from './error/error-page';
import {PromptRevisionHeader} from './QA/prompts/PromptRevisionHeader';
import {SectionPromptQuestions} from './QA/prompts/SectionPromptQuestions';

import {LoadingSpinner} from '../components/LoadingSpinner';
import {PageContainer} from '../components/PageContainer';
import {useActionConfirmation} from '../hooks/modal/action-confirmation';
import PromptEvaluationModal from '../hooks/modal/prompt-report-fullscreen-modal';
import {useNotificationContext} from '../providers/NotificationContextProvider';
import {DEFAULT_PROMPT_ID} from '../services/constants';
import {formatDuration} from '../services/utils';
import {useUserStore} from '../stores/userStore';
interface ViewPromptProps {}

export const PromptViewPage: FC<ViewPromptProps> = () => {
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const username = useUserStore((state) => state.username);
  const identityId = useUserStore((state) => state.userId);
  const callingIdentity = {identityId, username};
  const {setNotifications} = useNotificationContext();
  const {promptRevisionId} = useParams<{promptRevisionId: string}>();

  if (!promptRevisionId) throw new Error('this view requires a promptRevisionId');

  const isDefaultPrompt = promptRevisionId === DEFAULT_PROMPT_ID;
  const promptRevision = useGetPromptRevision({criteriaId: promptRevisionId});
  const publishedRevision = useGetPublishedPromptRevision();
  const publishPrompt = usePublishPromptRevision();
  const navigate = useNavigate();
  const promptRating = useGetPromptRatingAggregation({promptRevisionId});

  const onPublish = useCallback(async () => {
    await publishPrompt.mutateAsync({
      promptRevisionId,
      publishPromptRevisionRequestContent: {
        callingIdentity: callingIdentity as CallingIdentity,
      },
    });
    void promptRevision.refetch();
    void publishedRevision.refetch();
    setNotifications((notifications) => [
      ...notifications,
      {
        id: `published-${promptRevisionId}`,
        header: 'Published',
        content: 'Prompt revision has been published and will be used for future call summarization.',
        type: 'success',
      },
    ]);
  }, [promptRevisionId, setNotifications]);

  const publishConfirmation = useActionConfirmation({
    prompt: `Are you sure you wish to publish prompt ${promptRevision.data?.name}? All future contacts will be summarized using these prompts.`,
    confirmationButtonText: 'Publish',
    onConfirm: onPublish,
  });
  const isPublished = publishedRevision.data?.promptRevisionId === promptRevision.data?.promptRevisionId;

  if (promptRevision.isError || publishedRevision.isError) {
    return <ErrorPage errors={[promptRevision.error, publishedRevision.error]} />;
  }

  if (promptRevision.isLoading || publishedRevision.isLoading) {
    return (
      <div className={'flex flex-1 items-center justify-center'}>
        <LoadingSpinner size={'lg'} />
      </div>
    );
  }

  const action = (
    <>
      {!isDefaultPrompt && (
        <Button
          onClick={() => {
            navigate(`/prompts/edit/${promptRevision.data?.promptRevisionId}`);
          }}
          disabled={!permissionsMap[PERMISSIONS.PROMPT_UPDATE]}
        >
          Edit
        </Button>
      )}
      <Button
        variant={'outlined'}
        onClick={publishConfirmation.show}
        disabled={!permissionsMap[PERMISSIONS.PUBLISH_PROMPT_CREATE] || isPublished}
      >
        Publish
      </Button>
    </>
  );

  return (
    <PageContainer
      title={
        <div className={'flex items-center gap-2'}>
          {promptRevision.data.name}
          {promptRevision.data.duration && (
            <Chip
              size={'small'}
              label={formatDuration(promptRevision.data.duration)}
              sx={{
                textTransform: 'capitalize',
                backgroundColor: 'var(--mui-palette-warning-main)',
              }}
              icon={<AccessTimeIcon />}
            />
          )}
        </div>
      }
      subtitle={isDefaultPrompt ? 'Default Criteria' : promptRevisionId.replace('prompt-', 'criteria-')}
      actions={action}
      stack
    >
      {publishConfirmation.modal}
      <PromptRevisionHeader
        isPublished={isPublished}
        promptRevision={promptRevision.data}
        initialDirection={promptRevision.data.initialDirection}
      />
      {promptRevision.data.queueIds && Object.keys(promptRevision.data.queueIds).length > 0 && (
        <Card>
          <CardHeader title={'Queue Ids'} />
          <CardContent sx={{maxHeight: '150px', overflowY: 'auto'}}>
            {Object.entries(promptRevision.data.queueIds).map(([queueId, queueName]) => (
              <Chip
                color={'default'}
                label={queueName}
                key={queueId}
                sx={{marginRight: '1rem', marginBottom: '0.5rem'}}
              />
            ))}
          </CardContent>
        </Card>
      )}
      {Object.entries(promptRevision.data.promptGroups)
        .sort(([a], [b]) => {
          return b.localeCompare(a);
        })
        .map(([promptGroupName, promptGroup]) => {
          return (
            <SectionPromptQuestions
              key={promptGroupName}
              promptGroupName={promptGroupName}
              totalWeight={promptGroup.totalWeight}
              questions={promptGroup.questions}
              promptRevisionId={promptRevisionId}
              promptRating={promptRating.data?.feedback}
              promptGroupType={promptGroup.promptGroupType}
            />
          );
        })}
      <PromptEvaluationModal />
    </PageContainer>
  );
};
